import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ACCOUNT_ROLE_LABELS, type PlatformAccount } from '@azko/shared';

import { ApiRequestError, api } from '../../lib/api.js';
import { formatReceived } from '../../lib/format.js';
import { useSession } from '../../lib/session.js';
import { useDebouncedValue } from '../../lib/useDebouncedValue.js';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  LoadingRows,
  PageHeader,
  Pagination,
  SearchInput,
  StatusBadge,
  TextInput,
  cx,
  resetting,
  useCursorPages,
} from '../../components/ui.js';

/**
 * Contas do sistema — toda pessoa que entra no produto.
 *
 * A equipe so enxergava CONDOMINIOS. Quando a ligacao era sobre uma PESSOA — "o
 * porteiro do turno da noite nao consegue entrar", "trocamos de sindico e o
 * antigo ainda tem acesso" — nao havia por onde comecar: era preciso adivinhar
 * o condominio, abrir a conta e procurar la dentro.
 *
 * CONTA DE EQUIPE APARECE E NAO SE EDITA. Saber quem e da casa e trabalho
 * legitimo; mexer na senha de um colega e escalada de privilegio — sem essa
 * regra, esta tela seria o caminho mais curto entre "suporte" e "admin da
 * plataforma". A API recusa antes de a tela recusar, e os botoes somem para
 * que ninguem descubra a regra levando um 403 com o cliente no telefone.
 */

const KINDS = [
  { value: 'all', label: 'Todas' },
  { value: 'client', label: 'Clientes' },
  { value: 'staff', label: 'Equipe Azko' },
] as const;

type Kind = (typeof KINDS)[number]['value'];

export function AccountsPage() {
  const queryClient = useQueryClient();
  const { staff } = useSession();

  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<Kind>('all');
  const [open, setOpen] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  /** A senha recem-emitida, para o atendente ditar. Some ao fechar o painel. */
  const [issued, setIssued] = useState<{ id: string; password: string } | null>(null);

  const podeEditar = staff?.permissions.includes('plataforma:assinantes:editar') ?? false;
  const debouncedSearch = useDebouncedValue(search, 300);

  const pages = useCursorPages();
  // Buscar ou trocar o tipo volta para a primeira pagina: o cursor apontava
  // para uma linha da lista anterior e nao significa nada na nova.
  const changeSearch = resetting(pages, setSearch);
  const changeKind = resetting<Kind>(pages, setKind);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['admin', 'contas', debouncedSearch, kind, pages.cursor],
    queryFn: () =>
      api.get<{ items: PlatformAccount[]; nextCursor: string | null; total: number }>(
        `/admin/contas?kind=${kind}&limit=25${
          debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : ''
        }${pages.cursor ? `&cursor=${encodeURIComponent(pages.cursor)}` : ''}`,
      ),
    placeholderData: keepPreviousData,
  });

  function close() {
    setOpen(null);
    setReason('');
    setFailure(null);
    setIssued(null);
  }

  function onError(cause: unknown) {
    setFailure(cause instanceof ApiRequestError ? cause.message : 'Nao consegui fazer isso.');
  }

  const resetPassword = useMutation({
    mutationFn: (account: PlatformAccount) =>
      api.post<{ email: string; password: string }>(`/admin/contas/${account.id}/senha`, {
        reason: reason.trim(),
      }),
    onSuccess: (result, account) => {
      setIssued({ id: account.id, password: result.password });
      setReason('');
      setFailure(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'contas'] });
    },
    onError,
  });

  const setAccess = useMutation({
    mutationFn: ({ account, active }: { account: PlatformAccount; active: boolean }) =>
      api.post<PlatformAccount>(`/admin/contas/${account.id}/acesso`, {
        active,
        reason: reason.trim(),
      }),
    onSuccess: () => {
      close();
      void queryClient.invalidateQueries({ queryKey: ['admin', 'contas'] });
    },
    onError,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <PageHeader title="Contas do sistema" />

      <Card>
        <CardHeader
          title="Quem entra no produto"
          subtitle={`${total} ${total === 1 ? 'conta' : 'contas'}${
            debouncedSearch ? ' nesta busca' : ' — sindico, porteiro e equipe Azko'
          }`}
        />

        <div className="space-y-3 px-5 pb-5 sm:px-6">
          <SearchInput
            value={search}
            onChange={(event) => changeSearch(event.target.value)}
            placeholder="Buscar por nome ou e-mail"
            aria-label="Buscar contas"
          />

          <div className="flex flex-wrap gap-2">
            {KINDS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  changeKind(option.value);
                  close();
                }}
                className={
                  kind === option.value
                    ? 'rounded-pill border border-accent bg-accent/10 px-3 py-1.5 text-sm font-semibold text-accent'
                    : 'rounded-pill border border-line bg-surface-2 px-3 py-1.5 text-sm font-semibold text-muted transition hover:text-fg'
                }
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {isLoading && <LoadingRows rows={5} />}

        {!isLoading && items.length === 0 && (
          <EmptyState
            title="Nenhuma conta"
            description={
              debouncedSearch
                ? 'Ninguem com esse nome ou e-mail.'
                : 'Nenhuma conta cadastrada neste filtro.'
            }
          />
        )}

        {items.length > 0 && (
          <ul className="divide-y divide-line-soft border-t border-line-soft">
            {items.map((conta) => {
              const aberta = open === conta.id;
              const daCasa = conta.kind === 'staff';

              return (
                <li key={conta.id} className="p-4 sm:px-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="flex flex-wrap items-center gap-2">
                        <span className="font-bold">{conta.name}</span>
                        {daCasa && <StatusBadge tone="accent">equipe Azko</StatusBadge>}
                        {!conta.active && <StatusBadge tone="warn">sem acesso</StatusBadge>}
                        {conta.mustChangePassword && (
                          <StatusBadge tone="neutral">troca de senha pendente</StatusBadge>
                        )}
                      </p>
                      <p className="mt-0.5 truncate text-sm text-muted">{conta.email}</p>

                      <p className="mt-1 text-sm text-faint">
                        {daCasa
                          ? conta.staffRoles.join(', ')
                          : conta.memberships.length === 0
                            ? 'sem vinculo com condominio'
                            : conta.memberships
                                .map(
                                  (link) =>
                                    `${ACCOUNT_ROLE_LABELS[link.role]} em ${link.condominiumName}`,
                                )
                                .join(' • ')}
                      </p>

                      <p className="mt-1 text-sm text-faint">
                        {/* "Nunca entrou" e a diferenca entre conta criada e
                            pessoa trabalhando — e a causa mais comum de "o
                            sistema nao funciona" no primeiro mes. */}
                        {conta.lastLoginAt
                          ? `ultimo acesso ${formatReceived(conta.lastLoginAt)}`
                          : 'nunca entrou'}
                      </p>
                    </div>

                    {podeEditar && !daCasa && (
                      <Button
                        variant="quiet"
                        className="shrink-0 py-1.5"
                        onClick={() => (aberta ? close() : (close(), setOpen(conta.id)))}
                      >
                        {aberta ? 'Fechar' : 'Administrar'}
                      </Button>
                    )}
                  </div>

                  {aberta && (
                    <div className="mt-3 space-y-3 rounded-2xl border border-line bg-surface-2 p-4">
                      {issued?.id === conta.id ? (
                        <div className="rounded-xl border border-accent/40 bg-accent/5 p-4">
                          <p className="text-sm font-bold">Senha nova gerada</p>
                          <p className="mt-2 font-mono text-2xl font-bold tracking-widest">
                            {issued.password}
                          </p>
                          {/* A senha aparece UMA vez. Ela nao fica guardada em
                              texto claro em lugar nenhum — o banco so tem o
                              hash —, entao fechar este painel a perde de vez. */}
                          <p className="mt-2 text-sm text-muted">
                            Dite agora: ela nao pode ser mostrada de novo. A pessoa vai ter de
                            trocar no primeiro acesso, e as sessoes abertas dela ja cairam.
                          </p>
                          <Button variant="ghost" className="mt-3 py-1.5" onClick={close}>
                            Pronto
                          </Button>
                        </div>
                      ) : (
                        <>
                          <TextInput
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                            placeholder="Motivo (obrigatorio)"
                            aria-label="Motivo da acao sobre a conta"
                            maxLength={255}
                          />

                          {failure && <Alert>{failure}</Alert>}

                          <div className="flex flex-wrap gap-2">
                            <Button
                              className="py-1.5"
                              disabled={reason.trim().length < 3}
                              loading={resetPassword.isPending}
                              onClick={() => resetPassword.mutate(conta)}
                            >
                              Gerar senha nova
                            </Button>

                            <Button
                              variant="ghost"
                              className="py-1.5"
                              disabled={reason.trim().length < 3}
                              loading={setAccess.isPending}
                              onClick={() =>
                                setAccess.mutate({ account: conta, active: !conta.active })
                              }
                            >
                              {conta.active ? 'Cortar acesso' : 'Devolver acesso'}
                            </Button>
                          </div>

                          <p className={cx('text-sm', conta.active ? 'text-muted' : 'text-faint')}>
                            {conta.active
                              ? 'Cortar acesso derruba as sessoes abertas e vale para TODOS os condominios desta pessoa.'
                              : 'Devolver acesso libera a entrada nos vinculos que ela ja tinha.'}
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {(pages.hasPrevious || data?.nextCursor) && (
          <Pagination
            pages={pages}
            nextCursor={data?.nextCursor ?? null}
            loading={isFetching}
            previousLabel="Anteriores"
            nextLabel="Proximas"
          />
        )}
      </Card>

      {kind !== 'client' && (
        <p className="text-sm text-faint">
          Conta da equipe Azko aparece aqui para consulta e nao se administra por esta tela —
          senha e acesso de staff sao alterados por quem administra a plataforma.
        </p>
      )}
    </>
  );
}
